import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../css/AdminNavbar.css';

const AdminNavbar = ({ darkMode, toggleTheme, setIsAdmin }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [adminEmail, setAdminEmail] = useState('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    // Get admin email from localStorage
    const email = localStorage.getItem('adminUsername');
    if (email) {
      setAdminEmail(email);
    }
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    localStorage.removeItem('adminLoginTime');
    setIsAdmin(false);
    setShowLogoutConfirm(false);
    navigate('/sec-pd-mgmt/login');
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  const isActive = (path) => {
    return location.pathname === path ? 'active' : '';
  };

  return (
    <>
      <nav className={`navbar navbar-expand-lg ${darkMode ? 'navbar-dark bg-dark' : 'navbar-light bg-white'} border-bottom sticky-top shadow-sm`}>
        <div className="container-fluid">
          {/* Brand */}
          <Link className="navbar-brand fw-bold" to="/sec-pd-mgmt/dashboard">
            <i className="fas fa-building me-2 text-primary"></i>
            <span>Prathamesh Admin</span>
          </Link>

          {/* Toggler for mobile */}
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#adminNavbar"
            aria-controls="adminNavbar"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          {/* Navbar content */}
          <div className="collapse navbar-collapse" id="adminNavbar">
            {/* Main Navigation Links */}
            <ul className="navbar-nav me-auto">
              <li className="nav-item">
                <Link 
                  className={`nav-link ${isActive('/sec-pd-mgmt/dashboard')}`} 
                  to="/sec-pd-mgmt/dashboard"
                  title="Dashboard"
                >
                  <i className="fas fa-tachometer-alt me-1"></i>
                  <span className="d-none d-lg-inline">Dashboard</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link ${isActive('/sec-pd-mgmt/properties')}`} 
                  to="/sec-pd-mgmt/properties"
                  title="Manage Properties"
                >
                  <i className="fas fa-building me-1"></i>
                  <span className="d-none d-lg-inline">Properties</span>
                </Link>
              </li>
              <li className="nav-item">
                <Link 
                  className={`nav-link ${isActive('/sec-pd-mgmt/queries')}`} 
                  to="/sec-pd-mgmt/queries"
                  title="Customer Queries"
                >
                  <i className="fas fa-envelope me-1"></i>
                  <span className="d-none d-lg-inline">Queries</span>
                </Link>
              </li>
            </ul>

            {/* Right side - Theme toggle and User menu */}
            <ul className="navbar-nav">
              {/* Theme Toggle */}
              <li className="nav-item">
                <button 
                  className={`btn btn-sm ${darkMode ? 'btn-outline-light' : 'btn-outline-secondary'} me-2`} 
                  onClick={toggleTheme}
                  title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
                  aria-label="Toggle theme"
                >
                  {darkMode ? (
                    <i className="fas fa-sun"></i>
                  ) : (
                    <i className="fas fa-moon"></i>
                  )}
                </button>
              </li>

              {/* User Dropdown Menu */}
              <li className="nav-item dropdown">
                <a 
                  className="nav-link dropdown-toggle d-flex align-items-center" 
                  href="#!" 
                  role="button" 
                  data-bs-toggle="dropdown" 
                  aria-expanded="false"
                  title="Admin Menu"
                >
                  <i className="fas fa-user-circle me-1"></i>
                  <span className="d-none d-lg-inline small">{adminEmail ? adminEmail.split('@')[0] : 'Admin'}</span>
                </a>
                <ul className={`dropdown-menu dropdown-menu-end ${darkMode ? 'dropdown-menu-dark' : ''}`}>
                  <li>
                    <span className="dropdown-header">
                      <i className="fas fa-envelope me-1 text-muted"></i>
                      {adminEmail}
                    </span>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <Link 
                      className="dropdown-item" 
                      to="/sec-pd-mgmt/profile"
                      title="View Profile"
                    >
                      <i className="fas fa-user me-2 text-primary"></i>
                      Profile
                    </Link>
                  </li>
                  <li>
                    <Link 
                      className="dropdown-item" 
                      to="/sec-pd-mgmt/change-password"
                      title="Change Password"
                    >
                      <i className="fas fa-key me-2 text-warning"></i>
                      Change Password
                    </Link>
                  </li>
                  <li>
                    <Link 
                      className="dropdown-item" 
                      to="/sec-pd-mgmt/settings"
                      title="Settings"
                    >
                      <i className="fas fa-cog me-2 text-secondary"></i>
                      Settings
                    </Link>
                  </li>
                  <li><hr className="dropdown-divider" /></li>
                  <li>
                    <button 
                      className="dropdown-item text-danger" 
                      onClick={handleLogoutClick}
                      title="Logout"
                    >
                      <i className="fas fa-sign-out-alt me-2"></i>
                      Logout
                    </button>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Confirm Logout
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={cancelLogout}
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to logout? You'll need to login again to access the admin panel.
                </p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={cancelLogout}
                >
                  <i className="fas fa-times me-1"></i>
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={confirmLogout}
                >
                  <i className="fas fa-sign-out-alt me-1"></i>
                  Yes, Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminNavbar;
